import { useEffect, useMemo, useState } from "react";
import { RcaApiError, type RcaClient } from "@rca/sdk";
import {
  describeSchema,
  flattenOperations,
  type Json,
  type OpenApiOperation,
  type SchemaField,
} from "./openapi.js";

/**
 * API ドキュメント（FR-W9）。
 *
 * **サーバの `/openapi.json` を読んで描く。** 画面側に第 2 の一覧を書き写さない。
 * ルート定義と OpenAPI のズレは既に CI が落とすので、ここに写すと三重管理になり、
 * 必ずどれかが古くなる。
 *
 * 描画ライブラリは使わない（数百 KB 増えるし、他の画面と見た目が揃わない）。
 */

const METHOD_TONE: Record<string, string> = {
  get: "ok",
  post: "",
  put: "warn",
  patch: "warn",
  delete: "danger",
};

export function ApiDocsView({ client }: { client: RcaClient }): React.JSX.Element {
  const [doc, setDoc] = useState<Json | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .openapi()
      .then((value) => {
        if (!cancelled) setDoc(value);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof RcaApiError ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const operations = useMemo(() => (doc === null ? [] : flattenOperations(doc)), [doc]);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === "") return operations;
    return operations.filter((op) =>
      `${op.method} ${op.path} ${op.group} ${op.summary}`.toLowerCase().includes(needle),
    );
  }, [filter, operations]);

  const groups = useMemo(() => {
    const map = new Map<string, OpenApiOperation[]>();
    for (const op of shown) {
      const list = map.get(op.group) ?? [];
      list.push(op);
      map.set(op.group, list);
    }
    return [...map.entries()];
  }, [shown]);

  const info = (doc?.["info"] ?? {}) as { title?: string; version?: string; description?: string };
  const bearer = bearerDescription(doc);

  return (
    <section className="stack">
      <header className="row">
        <h2 style={{ margin: 0, fontSize: 16 }}>{info.title ?? "API"}</h2>
        {info.version !== undefined && <span className="badge mono">v{info.version}</span>}
        <span className="spacer" style={{ flex: 1 }} />
        <a className="mono" href="/openapi.json" target="_blank" rel="noreferrer">
          /openapi.json
        </a>
      </header>

      {error !== null && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}

      {info.description !== undefined && <p className="muted">{info.description}</p>}

      {bearer !== null && (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          認証: <code>Authorization: Bearer &lt;トークン&gt;</code>
          {bearer === "" ? "" : ` — ${bearer}`}
        </p>
      )}

      <div className="filters">
        <input
          aria-label="エンドポイント検索"
          placeholder="パス・メソッド・説明で絞り込む"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: 320 }}
        />
        <span className="muted mono">{shown.length} 件</span>
      </div>

      {doc === null && error === null && <p className="muted">読み込み中…</p>}

      {groups.map(([group, ops]) => (
        <div key={group} className="stack">
          <h3 className="mono" style={{ margin: "8px 0 0", fontSize: 13 }}>
            {group}
          </h3>
          {ops.map((op) => {
            const key = `${op.method}-${op.path}`;
            return (
              <div key={key} className="card stack" style={{ gap: 6 }}>
                <button
                  type="button"
                  className="api-row"
                  onClick={() => setOpen((current) => (current === key ? null : key))}
                >
                  <span className={`badge ${METHOD_TONE[op.method] ?? ""}`}>
                    {op.method.toUpperCase()}
                  </span>
                  <code className="mono">{op.path}</code>
                  <span className="muted">{op.summary}</span>
                </button>

                {open === key && (
                  <OperationDetail doc={doc ?? {}} operation={op} testId={`operation-${key}`} />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </section>
  );
}

function bearerDescription(doc: Json | null): string | null {
  const schemes = (doc?.["components"] as Json | undefined)?.["securitySchemes"] as
    | Json
    | undefined;
  const bearer = schemes?.["bearerAuth"] as { description?: string } | undefined;
  if (bearer === undefined) return null;
  return bearer.description ?? "";
}

function OperationDetail({
  doc,
  operation,
  testId,
}: {
  doc: Json;
  operation: OpenApiOperation;
  testId: string;
}): React.JSX.Element {
  const bodyFields = describeSchema(doc, operation.requestBody?.schema);

  return (
    <div className="stack" data-testid={testId} style={{ gap: 8 }}>
      {operation.description !== undefined && <p className="muted">{operation.description}</p>}

      {operation.parameters.length > 0 && (
        <div className="stack" style={{ gap: 4 }}>
          <strong style={{ fontSize: 12 }}>パラメータ</strong>
          <FieldTable
            rows={operation.parameters.map((p) => ({
              name: p.name,
              type: `${p.type}（${p.in}）`,
              required: p.required,
              ...(p.description === undefined ? {} : { description: p.description }),
            }))}
          />
        </div>
      )}

      {operation.requestBody !== undefined && (
        <div className="stack" style={{ gap: 4 }}>
          <strong style={{ fontSize: 12 }}>リクエスト本文</strong>
          <p className="muted mono" style={{ margin: 0, fontSize: 11 }}>
            {operation.requestBody.contentType}
            {operation.requestBody.required ? "（必須）" : ""}
          </p>
          {bodyFields.length > 0 ? (
            <FieldTable rows={bodyFields} />
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              形の定義がありません（自由形）。
            </p>
          )}
        </div>
      )}

      <div className="stack" style={{ gap: 4 }}>
        <strong style={{ fontSize: 12 }}>応答</strong>
        <div className="table-wrap">
          <table>
            <tbody>
              {operation.responses.map((response) => (
                <tr key={response.status}>
                  <td className="mono" style={{ width: 60 }}>
                    {response.status}
                  </td>
                  <td>{response.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FieldTable({ rows }: { rows: readonly SchemaField[] }): React.JSX.Element {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>名前</th>
            <th>型</th>
            <th>必須</th>
            <th>説明</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td className="mono">{row.name}</td>
              <td className="mono">{row.type}</td>
              <td>{row.required ? "必須" : "-"}</td>
              <td>{row.description ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
